<script setup lang="ts">
import { API_GAME_DATA, API_GET_TAG_GAMES } from '@/api'
import { EGameType } from '@/types/components'
import type * as API_TYPE from '@/types/api'

const { isMobile } = useDevice()
const isShowAdsBlockTips = ref(false)
const route = useRoute()
const { t } = useI18n()

const urlGameName = route.params.name as string
if (!urlGameName)
  throw createError({ statusCode: 404, statusMessage: 'Page Not Found' })

const cacheKeyName = encodeURIComponent(urlGameName.substring(0, 50))
const { value, addToCache } = await useDataCache<API_TYPE.IGameData>(`api:game_detail_${cacheKeyName}`)
const { data: gameInfo, pending } = await useAsyncData(`api:game_detail_${cacheKeyName}`, async () => {
  if (value)
    return value

  const response = await API_GAME_DATA(urlGameName)
  if (response?.gameCategory?.length) {
    const tagGames = await API_GET_TAG_GAMES(response.gameCategory[0].categoryName)
    response.gameCategory[0].games = tagGames.games.filter(i => i.seo !== urlGameName).slice(0, 13)
  }

  await addToCache(response, [`game-detail:${response.details.gameId}`], 900)
  return response
}, {
  transform: (data) => {
    try {
      data.details.photos = JSON.parse(data.details.photos || '[]')[0]
    }
    catch (err) {}
    return data
  },
  getCachedData: (key, nuxt): API_TYPE.ReturnPromiseType<typeof API_GAME_DATA> => nuxt.payload.data[key] || nuxt.static.data[key],
  lazy: process.client,
})

watchEffect(() => {
  const cloudGamePathOpenWebGames = route.name === 'cloud-game' && gameInfo.value?.details.gameType === EGameType.WEBSITE
  const webGamePathOpenCloudGames = route.name === 'html5-games' && gameInfo.value?.details.gameType === EGameType.CLOUD

  if (webGamePathOpenCloudGames || cloudGamePathOpenWebGames)
    throw createError({ statusCode: 404, statusMessage: 'Page Not Found', fatal: true })
})

const bottomAdsId = ref('7592')
const gameName = computed(() => gameInfo.value?.details.gameName || 'Game')

const title = computed(() => gameInfo.value?.details.gameType === EGameType.WEBSITE ? t('title.websiteGame', { gameName: gameName.value }) : t('title.cloudGame', { gameName: gameName.value }, `Play ${gameName.value} Online | EasyFun.gg`))
const description = computed(() => gameInfo.value?.details.gameType === EGameType.WEBSITE ? t('description.websiteGame', { gameName: gameName.value }, `Play this funny browser game ${gameName.value} instantly with EasyFun.gg. Immerse yourself in a high-quality gaming experience right in your browser.`) : t('description.cloudGame', { gameName: gameName.value }, `Run ${gameName.value} instantly online for free in your browser with EasyFun.gg. Enjoy a smooth, high-quality gaming experience without the need for downloading.`))
useSeoMeta({
  title,
  ogTitle: title,
  description,
  ogDescription: description,
  // viewport: `${gameInfo.value?.details.screenOrientation === EGameOrientation.PORTRAIT ? 'width=device-width' : 'width=device-height'},initial-scale=1,maximum-scale=1,minimum-scale=1,user-scalable=0`,
})

useCacheRoute(undefined, (helper) => {
  helper.setMaxAge(300).setCacheable().addTags([`game-detail:${gameInfo.value?.details.gameId}`])
})

useHead(computed(() => ({
  link: [{ rel: 'manifest', href: gameInfo.value ? `/manifest.webmanifest?name=${gameName.value}&icon=${gameInfo.value.details.icon}&path=${route.path}` : '' }],
})))

onMounted(() => {
  if (window.innerWidth >= 1920)
    bottomAdsId.value = '7593'

  // isShowAdsBlockTips.value = !window.EASYFUN_ADS_CAN_RUN && process.env.NODE_ENV !== 'development'
})

if (process.client) {
  // pwa installed ga 上报
  window.addEventListener('appinstalled', () => {
    window?.dataLayer?.push({ event: 'install-desktop' })
  })
}

watchEffect(() => {
  if (isShowAdsBlockTips.value) {
    try {
      window?.dataLayer?.push({ event: 'pop-up', type: 99 })
    }
    catch (e) {}
  }
})
</script>

<template>
  <div class="relative view-content flex justify-center filled-content" mt="3 md:6" :class="isMobile && 'p-0!'">
    <div class="flex-1" w="100% lg:[calc(100%-340px)]">
      <!-- playground -->
      <GamePlayGround v-if="!pending && gameInfo?.details" :detail="gameInfo.details" />
      <div v-else class="relative aspect-ratio-880/496 w-full flex-center of-hidden rd-4 bg-gray-2" />

      <ClientOnly>
        <template #placeholder>
          <div class="ads-h mt-10 h-20.5 w-full lt-lg:hidden" />
        </template>
        <div class="ads-h mt-10 h-20.5 w-full flex-center animate-keyframes-fade-in animate-duration-1000 rd-4 bg-gray-2 lt-lg:hidden">
          <ins class="stpdwrapper" :data-tag-id="bottomAdsId" />
          <AdsBlockTips />
        </div>
      </ClientOnly>

      <!-- recommend tags and games -->
      <GameTagGroup v-if="gameInfo?.gameCategory?.length" :tags="gameInfo.gameCategory" :class="isMobile && 'px-5'" />

      <!-- game intro -->
      <GameIntro :content="gameInfo?.details?.content" :game-name="gameName" :control-explain="gameInfo?.details?.description || undefined" class="mt-10 md:mt-20" :class="isMobile && 'px-5'" :is-web-game="gameInfo?.details?.gameType === EGameType.WEBSITE" />
    </div>
    <!-- ads -->
    <!-- <BuildOnly> -->
    <GameAds />
    <!-- </BuildOnly> -->
  </div>
  <CommonAlert v-if="isShowAdsBlockTips" :title="$t('gameDetail.adBlockTitle')" :content="$t(`gameDetail.adBlockContent`)" :btn-text="$t(`gameDetail.adBlockBtn`)" class="fixed" @close="isShowAdsBlockTips = false" @confirm="isShowAdsBlockTips = false" />
</template>

<style lang="scss" scoped>
@media only screen and (min-width: 1920px) {
  .game {
    width: calc(100% - 320px);
  }
  .ads-h {
    height: 122px;
  }
}
</style>
